/* Native scroll effects; no animation framework, tracking, or form submissions. */
(() => {
  'use strict';
  const init = root => {
    if (!root || root.__penguinV6Initialized) return;
    root.__penguinV6Initialized = true;
    root.dataset.initialized = 'true';
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = window.matchMedia('(max-width: 700px)');
    const motionButton = root.querySelector('[data-motion-toggle]');
    const motionLabel = root.querySelector('[data-motion-label]');
    let preference = root.dataset.defaultMotion || 'on';
    try { preference = localStorage.getItem('penguin-v6-motion') || root.dataset.defaultMotion || 'on'; } catch (_) {}
    let motion = preference !== 'off' && !reduce.matches;
    const observers = [];
    const controller = new AbortController();
    const listen = (el, event, callback, options = {}) => el && el.addEventListener(event, callback, {...options, signal:controller.signal});
    // A blocked logo request must not leave a broken-image icon or a fake logo.
    // Only the plain brand name is shown on failure; successful/cached images win.
    root.querySelectorAll('[data-brand-logo-image]').forEach(img => {
      const card = img.closest('[data-brand-logo-card]');
      const frame = card?.querySelector('.p-brand-image-frame');
      const fallback = card?.querySelector('.p-brand-logo-fallback');
      if (!frame || !fallback) return;
      const syncBrandImage = () => {
        const failed = img.complete && img.naturalWidth === 0;
        frame.hidden = failed;
        fallback.hidden = !failed;
      };
      listen(img, 'load', syncBrandImage);
      listen(img, 'error', syncBrandImage);
      syncBrandImage();
    });
    const setMotion = () => {
      motion = preference !== 'off' && !reduce.matches;
      root.classList.toggle('p-motion-on', motion);
      root.classList.toggle('p-motion-off', !motion);
      motionButton?.setAttribute('aria-pressed', String(motion));
      if (motionLabel) motionLabel.textContent = reduce.matches ? '画面の動き：端末設定でオフ' : `画面の動き：${motion ? 'オン' : 'オフ'}`;
      if (!motion) root.querySelectorAll('.p-pending').forEach(el => el.classList.remove('p-pending'));
    };
    setMotion();
    listen(motionButton, 'click', () => {
      preference = motion ? 'off' : 'on';
      try { localStorage.setItem('penguin-v6-motion', preference); } catch (_) {}
      setMotion();
      requestTick();
    });
    if (reduce.addEventListener) listen(reduce, 'change', () => { setMotion(); requestTick(); });
    const reveals = [...root.querySelectorAll('.p-reveal')];
    if ('IntersectionObserver' in window) {
      try {
        const revealObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.remove('p-pending');
              revealObserver.unobserve(entry.target);
            }
          });
        }, {threshold:0.07, rootMargin:'0px 0px -30px 0px'});
        reveals.forEach(el => {
          if (motion && el.getBoundingClientRect().top > window.innerHeight - 40) el.classList.add('p-pending');
          revealObserver.observe(el);
        });
        observers.push(revealObserver);
      } catch (_) { reveals.forEach(el => el.classList.remove('p-pending')); }
    }
    listen(root, 'focusin', event => event.target.closest('.p-pending')?.classList.remove('p-pending'));
    // In-page navigation retains native browser history and normal scrolling.
    const menu = root.querySelector('[data-menu-toggle]');
    const mobileNav = root.querySelector('[data-mobile-nav]');
    function closeMenu(restoreFocus = false) {
      if (!menu || !mobileNav) return;
      const wasOpen = menu.getAttribute('aria-expanded') === 'true';
      menu.setAttribute('aria-expanded', 'false'); menu.setAttribute('aria-label','メニューを開く'); mobileNav.hidden = true;
      if (restoreFocus && wasOpen) menu.focus();
    }
    listen(menu, 'click', () => {
      const opening = menu.getAttribute('aria-expanded') !== 'true';
      menu.setAttribute('aria-expanded', String(opening)); menu.setAttribute('aria-label', opening ? 'メニューを閉じる' : 'メニューを開く'); mobileNav.hidden = !opening;
    });
    listen(mobileNav,'click',event => { if(event.target.closest('a')) closeMenu(); });
    listen(document,'keydown',event => { if(event.key === 'Escape') closeMenu(true); });
    listen(root,'click',event => {
      const link = event.target.closest('a[href^="#"]');
      if (!link || !link.hash) return;
      const dest = root.querySelector(link.hash);
      if (dest) {
        dest.classList.remove('p-pending');
        dest.querySelectorAll('.p-pending').forEach(el => el.classList.remove('p-pending'));
      }
      if (!motion && dest) {
        event.preventDefault();
        dest.scrollIntoView({behavior:'instant', block:'start'});
        try { history.pushState(null,'',link.hash); } catch (_) {}
      }
    });
    const story = root.querySelector('[data-story]');
    const steps = [...root.querySelectorAll('[data-step]')];
    const pips = [...root.querySelectorAll('[data-story-pip]')];
    const stageNumber = root.querySelector('[data-stage-number]');
    let active = -1;
    function setStep(index) {
      if (index === active) return;
      active = index;
      if (story) story.dataset.active = String(index);
      steps.forEach((el,i) => el.setAttribute('data-active', String(i === index)));
      pips.forEach((el,i) => i === index ? el.setAttribute('aria-current','step') : el.removeAttribute('aria-current'));
      if(stageNumber) stageNumber.textContent = `0${index + 1}`;
    }
    setStep(0);
    const navSections = [...root.querySelectorAll('[data-nav-section]')];
    const navLinks = [...root.querySelectorAll('.p-nav-links a[href^="#"]')];
    const hero = root.querySelector('.p-hero');
    const productStage = root.querySelector('[data-product-stage]');
    const teeObject = root.querySelector('.p-tee-object');
    const shoeObject = root.querySelector('.p-shoe-object');
    const art = root.querySelector('.p-art-inner');
    const footer = root.querySelector('.p-footer');
    const final = root.querySelector('.p-final');
    const mobileCta = root.querySelector('.p-mobile-cta');
    const progress = root.querySelector('.p-progress');
    const nativeTimeline = window.CSS && CSS.supports('animation-timeline','scroll()');
    let ticking = false;
    function update() {
      ticking = false;
      const vh = window.innerHeight;
      if (productStage && teeObject && shoeObject) {
        const heroRect = hero.getBoundingClientRect();
        if (heroRect.bottom > 0 && heroRect.top < vh) {
          const t = Math.max(0, Math.min(1, -heroRect.top / Math.max(1, heroRect.height)));
          teeObject.style.transform = motion ? `translate3d(0,${t * -24}px,0) rotate(${7 - t * 3}deg)` : 'none';
          shoeObject.style.transform = motion ? `translate3d(0,${t * -10}px,0) rotate(${-12 + t * 2}deg)` : 'none';
        }
      }
      if (!mobile.matches && steps.length) {
        let index = 0;
        steps.forEach((step,i) => { if (step.getBoundingClientRect().top < vh * .6) index = i; });
        setStep(index);
      }
      if (!nativeTimeline && motion) {
        const y = window.scrollY;
        const max = Math.max(1, document.documentElement.scrollHeight - vh);
        if(progress) progress.style.transform = `scaleX(${Math.max(0,Math.min(1,y / max))})`;
        if(art && !mobile.matches && hero && hero.getBoundingClientRect().bottom > 0) art.style.transform = `translateY(${Math.min(80,y * .094)}px)`;
      } else if (art && (!motion || mobile.matches)) { art.style.transform = ''; }
      if (mobileCta && hero && final) {
        const show = mobile.matches && hero.getBoundingClientRect().bottom < 75 && final.getBoundingClientRect().top > vh;
        mobileCta.classList.toggle('is-visible', show);
        mobileCta.inert = !show;
        mobileCta.setAttribute('aria-hidden', String(!show));
      }
      let current = '';
      navSections.forEach(section => { if (section.getBoundingClientRect().top <= 170) current = '#' + section.id; });
      navLinks.forEach(link => link.hash === current ? link.setAttribute('aria-current','location') : link.removeAttribute('aria-current'));
    }
    function requestTick() { if (!ticking) { ticking = true; requestAnimationFrame(update); } }
    pips.forEach((pip,i) => listen(pip,'click',event => {
      event.preventDefault();
      if (mobile.matches) { setStep(i); }
      else if (steps[i]) {
        const top = window.scrollY + steps[i].getBoundingClientRect().top - window.innerHeight * .46;
        window.scrollTo({top: Math.max(0,top), behavior: motion ? 'smooth' : 'instant'});
      }
    }));
    listen(window,'scroll',requestTick,{passive:true});
    listen(window,'resize',() => { if(window.innerWidth > 950) closeMenu(); requestTick(); },{passive:true});
    listen(window,'pageshow',requestTick);
    const productImages = [...root.querySelectorAll('.p-product img')];
    const imageStatus = root.querySelector('[data-image-status]');
    function checkImages() {
      productImages.forEach(img => img.dataset.loadFailed = String(img.complete && img.naturalWidth === 0));
      const failed = productImages.some(img => img.complete && img.naturalWidth === 0);
      if (imageStatus) imageStatus.hidden = !failed;
    }
    productImages.forEach(img => { listen(img,'load',checkImages); listen(img,'error',checkImages); });
    listen(root.querySelector('[data-retry-images]'),'click',() => {
      if (imageStatus) imageStatus.hidden = true;
      productImages.forEach(img => { if(img.naturalWidth === 0) { const src=img.getAttribute('src'); img.removeAttribute('src'); requestAnimationFrame(() => {img.setAttribute('src',src);}); } });
    });
    checkImages();
    update();
    const dialog = root.querySelector('[data-demo-dialog]');
    const demoButton = root.querySelector('[data-demo-open]');
    listen(demoButton,'click',() => { if(dialog && typeof dialog.showModal === 'function') dialog.showModal(); });
    root.querySelectorAll('[data-demo-close]').forEach(el => listen(el,'click',() => dialog?.close()));
    listen(dialog,'click',event => {
      if(event.target === dialog) {
        const r = dialog.getBoundingClientRect();
        if(event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close();
      }
    });
    // Shopify Theme Editor may mount/unmount a section without a full page load.
    const unload = event => {
      if(event.target.contains(root)) {
        observers.forEach(observer => observer.disconnect()); controller.abort();
        root.__penguinV6Initialized = false;
        delete root.dataset.initialized;
        document.removeEventListener('shopify:section:unload',unload);
      }
    };
    document.addEventListener('shopify:section:unload',unload);
  };
  document.addEventListener('shopify:section:select', event => {
    event.target.querySelectorAll('.p-pending').forEach(el => el.classList.remove('p-pending'));
  });
  const start = () => document.querySelectorAll('#pk6').forEach(init);
  if(document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start,{once:true}); else start();
  document.addEventListener('shopify:section:load',event => { const root = event.target.querySelector('#pk6'); if(root) init(root); });
})();
